import { Link } from "react-router-dom";
import { makeStyles } from "tss-react/mui";

/**
 * A customizable link component built on top of `react-router-dom`'s `Link`.
 * Supports custom styling, disabled state, and click handling.
 *
 * @component
 * @param {Object} props - The component props.
 * @param {string} [props.className] - Additional CSS class to apply to the link.
 * @param {string} [props.title] - The text or content to display inside the link.
 * @param {string} [props.color] - The color of the link text. Defaults to the theme's blue color.
 * @param {boolean} [props.disabled] - Whether the link is disabled. If `true`, the link will have reduced opacity and no click handler.
 * @param {Object} [props.style] - Inline styles to apply to the link.
 * @param {Function} [props.onClick] - Click event handler for the link. Will not fire if the link is disabled.
 * @param {Object} [props.rest] - Additional props to pass to the underlying `Link` component.
 * @param {string} [props.to] - The destination URL or path for the link.
 * @returns {JSX.Element} - The rendered `MiLink` component.
 *
 * @example
 * // Basic usage
 * <MiLink to="/home" title="Home" />
 *
 * @example
 * // Custom color and disabled state
 * <MiLink to="/about" title="About" color="red" disabled />
 *
 * @example
 * // With click handler
 * <MiLink to="/contact" title="Contact" onClick={() => alert('Clicked!')} />
 */
export const MiLink = ({
  className,
  title,
  color,
  disabled,
  style,
  onClick,
  ...rest
}) => {
  const { classes } = useStyles({
    color,
    disabled,
  });

  const handleClick = (e) => {
    if (onClick && !disabled) {
      onClick(e);
    }
  };

  return (
    <Link
      className={className ? [classes.link, className].join(" ") : classes.link}
      onClick={handleClick}
      style={style}
      {...rest}
    >
      {title}
    </Link>
  );
};

const useStyles = makeStyles()((theme, { color, disabled }) => ({
  link: {
    opacity: disabled ? 0.3 : 1,
    color: color || theme.palette.blue.main,
    cursor: disabled ? "default" : "pointer",
    whiteSpace: "nowrap",
    textDecoration: "none",
    "&:hover": { textDecoration: "underline" },
    textAlign: "center",
    flex: 0,
  },
}));
